import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { PieChart as PieChartIcon } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useChartTheme } from "@/shared/hooks/useChartTheme";
import { resolveChartColor, resolveChartData } from "@/shared/lib/resolveChartColor";
import { adminInvoiceDistribution } from "../../data/adminDashboard";
import { invoiceDistributionRoutes } from "../../navigation/dashboardRoutes";

type DistributionItem = { key: string; value: number; color: string };

export function InvoiceDistributionChart({
  data = adminInvoiceDistribution,
}: {
  data?: DistributionItem[];
}) {
  const { t } = useTranslation();
  const { isDark } = useChartTheme();
  const labels = t.safecheck.adminDashboard.invoiceDistribution.labels;

  const chartData = resolveChartData(
    data
      .filter((item) => item.value > 0)
      .map((item) => ({
        ...item,
        name: labels[item.key as keyof typeof labels] ?? item.key,
      })),
    isDark,
  );

  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <Link
      to="/dashboard/invoices"
      className="theme-card block p-5 transition-all hover:border-accent/40 hover:shadow-[var(--shadow)]"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">
          {t.safecheck.adminDashboard.invoiceDistribution.title}
        </h3>
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-soft">
          <PieChartIcon size={16} className="text-purple" />
        </div>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={55}
            outerRadius={85}
            dataKey="value"
            stroke="none"
            paddingAngle={2}
          >
            {chartData.map((entry) => (
              <Cell key={entry.key} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const item = payload[0];
              const pct = total > 0 ? (((item.value as number) / total) * 100).toFixed(1) : "0";
              return (
                <div className="chart-tooltip px-3 py-2 text-foreground">
                  <p className="text-xs font-medium">
                    {item.name}: {item.value} ({pct}%)
                  </p>
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>

      <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-2">
        {data.map((item) => (
          <Link
            key={item.key}
            to={invoiceDistributionRoutes[item.key as keyof typeof invoiceDistributionRoutes] ?? "/dashboard/invoices"}
            onClick={(event) => event.stopPropagation()}
            className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-primary"
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: resolveChartColor(item.color, isDark) }}
            />
            {labels[item.key as keyof typeof labels] ?? item.key}
          </Link>
        ))}
      </div>
    </Link>
  );
}
